import type { IntelWorkspaceItem } from '@pn/core/domain/intel/intel-workspace-item';
import { handleError } from '@pn/core/errors/handleError';
import { useIntelAccess } from '@pn/core/permissions/isIntelAccessDenied';
import { useDebouncedValue } from '@pn/core/utils/useDebouncedValue';
import React from 'react';
import {
  intelWorkspaceItemsActions,
  useIntelWorkspaceItemsStorage,
} from 'src/application/storage';
import { getIntelItemData } from './getIntelItemData';

const tokensById: Record<string, number> = {};

function getIntelData({
  intelWorkspaceItem,
  geometry,
  province,
}: {
  intelWorkspaceItem: IntelWorkspaceItem;
  geometry?: GeoJSON.Geometry;
  province?: string;
}) {
  const { id: intelQueryId, query } = intelWorkspaceItem;
  if (!tokensById[intelQueryId]) tokensById[intelQueryId] = 0;

  const thisToken = ++tokensById[intelQueryId];

  intelWorkspaceItemsActions().request(intelQueryId);

  getIntelItemData({
    intelWorkspaceItem,
    geometry,
    province,
  })
    .then((data) => {
      if (thisToken !== tokensById[intelQueryId]) {
        throw new Error('Not the latest request for this data type');
      }

      intelWorkspaceItemsActions().receive(intelQueryId, data);
    })
    .catch((error) => {
      if (error.message !== 'Not the latest request for this data type') {
        handleError({
          error,
          onError: () => intelWorkspaceItemsActions().error(intelQueryId),
          notify: `Failed to get ${query.dataType}`,
        });
      }
    });
}

export function useAutoLoadIntelData() {
  const { itemsToProcess, areaGeometryFilter, provinceFilter } =
    useIntelWorkspaceItemsStorage();

  const access = useIntelAccess();

  const [debouncedQueriesToProcess] = useDebouncedValue(itemsToProcess, 500);

  const queriesUserHasAccessTo = React.useMemo(() => {
    return debouncedQueriesToProcess.filter((query) =>
      access(query.query.dataType).granted()
    );
  }, [access, debouncedQueriesToProcess]);

  React.useEffect(() => {
    queriesUserHasAccessTo.forEach((intelWorkspaceItem) => {
      getIntelData({
        intelWorkspaceItem,
        geometry: areaGeometryFilter ?? undefined,
        province: provinceFilter ?? undefined,
      });
    });
  }, [areaGeometryFilter, queriesUserHasAccessTo, provinceFilter]);
}
